import { useState, useEffect, useCallback } from "react";
import { T, F, Over, Pill, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// TRESA s.27(2)-(3), O. Reg. 567/05 ss.2.2, 16: written deposit-terms disclosure per deposit
// (interest bearing? rate?), rate history, and interest owed per deal. Bricks drafts the
// disclosure and keeps the record; it does not send it to the depositor.
const cad = (cents) => new Intl.NumberFormat('en-CA', { style:'currency', currency:'CAD' }).format((cents || 0) / 100);
const day = (d) => d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-CA', { month:'short', day:'numeric', year:'numeric' }) : '—';
const METHODS = [['on_deposit_receipt','On the deposit receipt'],['email','Email'],['letter','Letter'],['in_person_signed','Signed in person'],['other','Other']];

export default function TrustInterest({ BASE, headers, p, dark }) {
  const [data, setData] = useState(null);
  const [recordFor, setRecordFor] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${BASE}/api/accounting/trust/interest`, { headers }).then(r => r.ok ? r.json() : null).then(setData).catch(() => setData(null));
  }, [BASE, headers.Authorization]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const download = async (entryId) => {
    const res = await fetch(`${BASE}/api/accounting/trust/interest/disclosure-text/${entryId}`, { headers });
    if (!res.ok) { toast.error('Couldn’t draft the disclosure'); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = url; a.download = `trust-deposit-disclosure-${String(entryId).slice(0, 8)}.txt`; a.click();
  };
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, interest_rate: form.interest_bearing ? String(form.interest_rate ?? '') : null };
      const res = await fetch(`${BASE}/api/accounting/trust/interest/disclosures`, { method:'POST', headers, body: JSON.stringify(body) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'That didn’t go through');
      toast.success('Disclosure recorded');
      (d.warnings || []).forEach(w => toast.warning(w));
      setRecordFor(null);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const open = (row) => {
    const t = data?.current_terms || {};
    setRecordFor(row);
    setForm({ entry_id: row.entry_id, depositor: row.payer || row.depositor || '', interest_bearing: !!t.interest_bearing,
              interest_rate: t.interest_rate ?? '', variable_rate: !!t.variable_rate, interest_to:'beneficial_owner',
              interest_payee:'', contract_clause:'', disclosed_on: (row.received_date || '').slice(0, 10), method:'on_deposit_receipt', reference:'' });
  };

  if (!data) return null;
  const missing = data.missing || [];
  const disclosures = data.disclosures || [];
  const byTrade = data.by_trade || [];
  const t = data.current_terms || {};
  const canManage = !!data.can_manage;
  const input = { marginTop:8, width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:p.bg, outline:'none', boxSizing:'border-box' };
  const small = (color) => ({ background:'none', border:`1px solid ${p.hair}`, borderRadius:2, padding:'4px 10px', cursor:'pointer', fontSize:11, fontFamily:F.mono, color });
  const row = (last) => ({ display:'grid', gridTemplateColumns:'1.6fr 1fr 1.4fr auto', gap:12, padding:'10px var(--bx-pad)', borderBottom: last ? 'none' : `1px solid ${p.hair}`, fontSize:13, alignItems:'center' });
  const head = { padding:'8px var(--bx-pad)', borderBottom:`1px solid ${p.hair}`, fontSize: 12, fontWeight: 500, color:p.ink4 };

  return (
    <div style={{ border:`1px solid ${p.hair}`, borderRadius:2 }}>
      <div style={{ padding:'16px var(--bx-pad)', borderBottom:`1px solid ${p.hair}` }}>
        <Over color={p.ink3}>Deposit disclosure &amp; interest · TRESA s.27(2)–(3)</Over>
        <div style={{ fontSize:13, color:p.ink2, marginTop:4 }}>
          Account today: {t.interest_bearing ? `interest bearing at ${t.interest_rate ?? '—'}%${t.variable_rate ? ' (variable; tell a depositor the current rate on request, O. Reg. 567/05 s.16)' : ''}` : 'not interest bearing'}
          {' · '}{missing.length ? `${missing.length} deposit${missing.length === 1 ? '' : 's'} without a recorded written disclosure` : 'Every deposit has a recorded disclosure'} · {data.honest_note}
        </div>
      </div>

      {missing.length > 0 && (<>
        <div style={head}>Needs a written disclosure</div>
        {missing.slice(0, 20).map((m, i) => (
          <div key={m.entry_id} style={row(i === Math.min(missing.length, 20) - 1)}>
            <div><div style={{ color:p.ink }}>{m.property_address || 'Deal'}</div><div style={{ fontSize:11, color:p.ink3, marginTop:2 }}>{m.payer || '—'} · received {day(m.received_date)}</div></div>
            <div style={{ fontFamily:F.mono }}>{cad(m.amount_cents)}</div>
            <div><Pill tone="warn" dark={dark}>No disclosure on file</Pill></div>
            <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
              <button style={small(p.ink2)} onClick={() => download(m.entry_id)}>Draft</button>
              {canManage && <button disabled={busy} style={small(T.brick)} onClick={() => open(m)}>Record given</button>}
            </div>
          </div>
        ))}
      </>)}

      {disclosures.length > 0 && (<>
        <div style={{ ...head, borderTop: missing.length ? `1px solid ${p.hair}` : 'none' }}>Disclosures on file</div>
        {disclosures.slice(0, 20).map((d, i) => (
          <div key={d.id} style={{ borderBottom: i === Math.min(disclosures.length, 20) - 1 ? 'none' : `1px solid ${p.hair}` }}>
            <div style={{ ...row(true) }}>
              <div><div style={{ color:p.ink }}>{d.property_address || 'Deal'}</div><div style={{ fontSize:11, color:p.ink3, marginTop:2 }}>{d.depositor} · {d.reference}</div></div>
              <div style={{ fontSize:12, color:p.ink2 }}>{d.interest_bearing ? `${d.interest_rate}%${d.variable_rate ? ' variable' : ''}` : 'No interest'}</div>
              <div style={{ fontSize:12, color:p.ink2 }}>Given {day(d.disclosed_on)} · {(METHODS.find(x => x[0] === d.method) || [0, d.method])[1]}{d.interest_to === 'other_per_contract' ? ` · interest to ${d.interest_payee} (${d.contract_clause})` : ''}</div>
              <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                <button style={small(p.ink2)} onClick={() => download(d.entry_id)}>Draft</button>
                {canManage && <button disabled={busy} style={small(p.ink3)} onClick={() => open({ ...d, payer: d.depositor })}>Replace</button>}
              </div>
            </div>
            {(d.warnings || []).length > 0 && <div style={{ padding:'0 24px 10px', fontSize:11, color:T.bad }}>{d.warnings.join(' ')}</div>}
          </div>
        ))}
      </>)}

      {byTrade.length > 0 && (<>
        <div style={{ ...head, borderTop:`1px solid ${p.hair}` }}>Interest by deal · O. Reg. 579/05 s.12 para 8</div>
        {byTrade.map((r, i) => (
          <div key={r.transaction_id} style={{ borderBottom: i === byTrade.length - 1 ? 'none' : `1px solid ${p.hair}` }}>
            <div style={{ display:'grid', gridTemplateColumns:'1.6fr 1fr 1fr 1.4fr', gap:12, padding:'10px var(--bx-pad)', fontSize:13 }}>
              <div style={{ color:p.ink }}>{r.property_address || 'Deal'}</div>
              <div style={{ fontFamily:F.mono, fontSize:12 }}>Earned {cad(r.earned_cents)}</div>
              <div style={{ fontFamily:F.mono, fontSize:12 }}>Paid {cad(r.paid_cents)}</div>
              <div style={{ fontSize:12, color: r.owing_cents > 0 ? T.brick : p.ink3 }}>{r.owing_cents > 0 ? `${cad(r.owing_cents)} owed to ${r.owed_to}` : 'Nothing owing'}</div>
            </div>
            {(r.warnings || []).length > 0 && <div style={{ padding:'0 24px 10px', fontSize:11, color:T.bad }}>{r.warnings.join(' ')}</div>}
          </div>
        ))}
      </>)}

      {missing.length === 0 && disclosures.length === 0 && byTrade.length === 0 && (
        <div style={{ padding:'24px var(--bx-pad)', fontSize:13, color:p.ink3 }}>No deposits yet. Each deposit received will need a written disclosure of the account's terms.</div>
      )}

      <Dialog open={!!recordFor} onOpenChange={(o) => !o && setRecordFor(null)}>
        <DialogContent style={{ maxWidth:520, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, color:p.ink }}>Record written disclosure</DialogTitle>
            <DialogDescription style={{ fontFamily:F.display, fontSize:13, color:p.ink3, marginTop:4 }}>
              Record what the depositor was told in writing. Interest goes to the beneficial owner unless a contract says otherwise.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display:'flex', flexDirection:'column', gap:14, paddingTop:18 }}>
            <div><Over color={p.ink3}>Depositor</Over><input style={input} value={form.depositor || ''} onChange={e => setForm({ ...form, depositor: e.target.value })}/></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, alignItems:'end' }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:p.ink2 }}>
                <input type="checkbox" checked={!!form.interest_bearing} onChange={e => setForm({ ...form, interest_bearing: e.target.checked })}/> Interest bearing
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:p.ink2 }}>
                <input type="checkbox" checked={!!form.variable_rate} disabled={!form.interest_bearing} onChange={e => setForm({ ...form, variable_rate: e.target.checked })}/> Variable
              </label>
              <div><Over color={p.ink3}>Rate (%)</Over><input type="number" step="0.01" style={{ ...input, fontFamily:F.mono }} disabled={!form.interest_bearing} value={form.interest_rate ?? ''} onChange={e => setForm({ ...form, interest_rate: e.target.value })}/></div>
            </div>
            <div><Over color={p.ink3}>Interest goes to</Over>
              <select style={input} value={form.interest_to || 'beneficial_owner'} onChange={e => setForm({ ...form, interest_to: e.target.value })}>
                <option value="beneficial_owner">Beneficial owner of the deposit</option>
                <option value="other_per_contract">Someone else, per the contract</option>
              </select>
            </div>
            {form.interest_to === 'other_per_contract' && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <div><Over color={p.ink3}>Paid to</Over><input style={input} value={form.interest_payee || ''} onChange={e => setForm({ ...form, interest_payee: e.target.value })}/></div>
                <div><Over color={p.ink3}>Contract clause</Over><input style={input} value={form.contract_clause || ''} onChange={e => setForm({ ...form, contract_clause: e.target.value })} placeholder="e.g. APS Schedule A, cl. 4"/></div>
              </div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div><Over color={p.ink3}>Given on</Over><input type="date" style={input} value={form.disclosed_on || ''} onChange={e => setForm({ ...form, disclosed_on: e.target.value })}/></div>
              <div><Over color={p.ink3}>How</Over>
                <select style={input} value={form.method || 'on_deposit_receipt'} onChange={e => setForm({ ...form, method: e.target.value })}>
                  {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            <div><Over color={p.ink3}>Reference</Over><input style={input} value={form.reference || ''} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder="e.g. Deposit receipt #1001, signed copy in deal file"/></div>
          </div>
          <DialogFooter style={{ marginTop:22, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setRecordFor(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy} onClick={save}>Save record</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
